// src/components/ImageViewer.tsx
import React, { useEffect, useRef, useState } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface ImageViewerProps {
    images: string[];
    initialIndex: number;
    onClose: () => void;
    productName?: string;
    productPrice?: number;
    productCode?: string;
    productDate?: string;
}

export const ImageViewer: React.FC<ImageViewerProps> = ({
                                                            images,
                                                            initialIndex,
                                                            onClose,
                                                            productName = '',
                                                            productPrice = 0,
                                                            productCode = '',
                                                            productDate = ''
                                                        }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [startTouch, setStartTouch] = useState({ x: 0, y: 0 });
    const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
    const [isSaving, setIsSaving] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Обработка зума (пинч)
    useEffect(() => {
        const handleTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2 && scale > 1) {
                e.preventDefault();
            }
        };

        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        return () => document.removeEventListener('touchmove', handleTouchMove);
    }, [scale]);

    // Закрытие по Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') prevImage();
            if (e.key === 'ArrowRight') nextImage();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, images.length]);

    const prevImage = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            resetZoom();
        }
    };

    const nextImage = () => {
        if (currentIndex < images.length - 1) {
            setCurrentIndex(currentIndex + 1);
            resetZoom();
        }
    };

    const resetZoom = () => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    };

    const handleDoubleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (scale > 1) {
            resetZoom();
        } else {
            setScale(2.5);
        }
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (scale > 1) {
            setIsDragging(true);
            const touch = e.touches[0];
            setStartTouch({ x: touch.clientX, y: touch.clientY });
            setStartPosition(position);
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (isDragging && scale > 1) {
            const touch = e.touches[0];
            const deltaX = touch.clientX - startTouch.x;
            const deltaY = touch.clientY - startTouch.y;

            const maxX = (scale * 100 - 100) / 2;
            const maxY = (scale * 100 - 100) / 2;

            let newX = startPosition.x + deltaX;
            let newY = startPosition.y + deltaY;

            newX = Math.min(Math.max(newX, -maxX), maxX);
            newY = Math.min(Math.max(newY, -maxY), maxY);

            setPosition({ x: newX, y: newY });
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    const handleWheel = (e: React.WheelEvent) => {
        if (e.ctrlKey) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            const newScale = Math.min(Math.max(scale + delta, 1), 4);
            setScale(newScale);
            if (newScale === 1) {
                setPosition({ x: 0, y: 0 });
            }
        }
    };

    const handleSwipe = (startX: number, endX: number) => {
        const diff = endX - startX;
        if (Math.abs(diff) > 50 && scale === 1) {
            if (diff > 0) {
                prevImage();
            } else {
                nextImage();
            }
        }
    };

    let touchStartX = 0;
    const onTouchStartSwipe = (e: React.TouchEvent) => {
        if (scale === 1) {
            touchStartX = e.touches[0].clientX;
        }
    };

    const onTouchEndSwipe = (e: React.TouchEvent) => {
        if (scale === 1) {
            const touchEndX = e.changedTouches[0].clientX;
            handleSwipe(touchStartX, touchEndX);
        }
    };

    // Функция для сохранения изображения с водяным знаком
    // Функция для сохранения изображения с водяным знаком
    const saveImageWithOverlay = async () => {
        setIsSaving(true);

        try {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.src = images[currentIndex];

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
            });

            const canvas = canvasRef.current;
            if (!canvas) return;

            // Устанавливаем размеры canvas
            const targetWidth = 1080;
            const targetHeight = 1350;
            canvas.width = targetWidth;
            canvas.height = targetHeight;

            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            // ========== HEADER (темно-зеленый) ==========
            ctx.fillStyle = '#1a4d2e';
            ctx.fillRect(0, 0, targetWidth, 80);

            ctx.fillStyle = '#FFFFFF';
            ctx.font = 'bold 36px "Segoe UI", "Arial", sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('RUYO-MARKET', targetWidth / 2, 52);

            // ========== ИЗОБРАЖЕНИЕ (притык к хедеру) ==========
            const imgY = 80;
            const availableHeight = targetHeight - 80 - 140; // минус хедер и футер
            const availableWidth = targetWidth;

            const imgAspect = img.width / img.height;
            let drawWidth = availableWidth;
            let drawHeight = availableWidth / imgAspect;

            if (drawHeight > availableHeight) {
                drawHeight = availableHeight;
                drawWidth = availableHeight * imgAspect;
            }

            const imgX = (targetWidth - drawWidth) / 2;

            ctx.drawImage(img, imgX, imgY, drawWidth, drawHeight);

            // ========== FOOTER (притык к изображению) ==========
            const footerY = imgY + drawHeight;
            ctx.fillStyle = '#1a4d2e';
            ctx.fillRect(0, footerY, targetWidth, 140);

            ctx.fillStyle = '#FFFFFF';
            ctx.font = 'bold 24px "Segoe UI", "Arial", sans-serif';
            ctx.textAlign = 'left';

            // Название товара
            let displayName = productName;
            if (displayName.length > 45) {
                displayName = displayName.substring(0, 42) + '...';
            }
            ctx.fillText(displayName, 30, footerY + 45);

            // Цена с надписью
            ctx.font = '20px "Segoe UI", "Arial", sans-serif';
            ctx.fillStyle = '#ffffff';
            ctx.fillText('Цена:', 30, footerY + 80);

            ctx.fillStyle = '#f59e0b';
            ctx.font = 'bold 24px "Segoe UI", "Arial", sans-serif';
            ctx.fillText(`${productPrice.toLocaleString()} TJS`, 90, footerY + 80);

            // Артикул
            ctx.fillStyle = '#cccccc';
            ctx.font = '16px "Segoe UI", "Arial", sans-serif';
            ctx.fillText(`Артикул: ${productCode || 'Не указан'}`, 30, footerY + 108);

            // Дата
            ctx.fillStyle = '#999999';
            ctx.font = '14px "Segoe UI", "Arial", sans-serif';
            ctx.fillText(productDate || new Date().toLocaleDateString('ru-RU'), 30, footerY + 128);

            // Копирайт
            ctx.fillStyle = '#f59e0b';
            ctx.font = 'bold 12px "Segoe UI", "Arial", sans-serif';
            ctx.textAlign = 'right';
            ctx.fillText('© RUYO-MARKET', targetWidth - 30, footerY + 125);

            // Конвертируем canvas в ссылку и скачиваем
            canvas.toBlob((blob) => {
                if (blob) {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `ruyo_${productCode || 'image'}_${Date.now()}.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    toast.success('Изображение сохранено!');
                }
            }, 'image/png');

        } catch (error) {
            console.error('Ошибка при сохранении изображения:', error);
            toast.error('Ошибка при сохранении');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="image-viewer-overlay" onClick={onClose}>
            <div className="image-viewer-container" onClick={(e) => e.stopPropagation()}>
                {/* Кнопка закрытия */}
                <button className="image-viewer-close" onClick={onClose}>
                    <XMarkIcon style={{ width: 24, height: 24 }} />
                </button>

                {/* Кнопка сохранения */}
                <button
                    className="image-viewer-save"
                    onClick={saveImageWithOverlay}
                    disabled={isSaving}
                >
                    <ArrowDownTrayIcon style={{ width: 22, height: 22 }} />
                    <span>{isSaving ? 'Сохранение...' : 'Сохранить'}</span>
                </button>

                {/* Кнопки навигации */}
                {currentIndex > 0 && (
                    <button className="image-viewer-nav prev" onClick={prevImage}>
                        <ChevronLeftIcon style={{ width: 32, height: 32 }} />
                    </button>
                )}
                {currentIndex < images.length - 1 && (
                    <button className="image-viewer-nav next" onClick={nextImage}>
                        <ChevronRightIcon style={{ width: 32, height: 32 }} />
                    </button>
                )}

                {/* Счетчик изображений */}
                <div className="image-viewer-counter">
                    {currentIndex + 1} / {images.length}
                </div>

                {/* Изображение */}
                <div
                    ref={containerRef}
                    className="image-viewer-content"
                    onDoubleClick={handleDoubleClick}
                    onTouchStart={(e) => {
                        onTouchStartSwipe(e);
                        handleTouchStart(e);
                    }}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={(e) => {
                        onTouchEndSwipe(e);
                        handleTouchEnd();
                    }}
                    onWheel={handleWheel}
                >
                    <img
                        ref={imageRef}
                        src={images[currentIndex]}
                        alt={`Изображение ${currentIndex + 1}`}
                        className="image-viewer-image"
                        style={{
                            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                            cursor: scale > 1 ? 'grab' : 'default',
                        }}
                        draggable={false}
                    />
                </div>

                {/* Инструкция */}
                {scale === 1 && (
                    <div className="image-viewer-instruction">
                        <span>👆 Свайп для переключения</span>
                        <span>🔍 Двойной тап для зума</span>
                    </div>
                )}
                {scale > 1 && (
                    <div className="image-viewer-instruction">
                        <span>✋ Тащите для перемещения</span>
                    </div>
                )}

                {/* Скрытый canvas для генерации изображения */}
                <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
        </div>
    );
};